import type { FilterSection } from '@fontbold/ui';
import type { CitySummary } from '#/lib/location';

/**
 * Map filters are driven entirely by what the API returns: cities and
 * districts from the location summary, tags from `availableCategories`/
 * `availableTags` on the place connection. Counts are faceted against the
 * current selection using each place's own labels.
 */

export type FilterState = {
  city: string[];
  district: string[];
  tag: string[];
};

export type FilterGroupKey = keyof FilterState;

const GROUPS: FilterGroupKey[] = ['city', 'district', 'tag'];

export const EMPTY_FILTERS: FilterState = { city: [], district: [], tag: [] };

export type FilterablePlace = {
  id: string;
  citySlug: string;
  districtSlug: string;
  tagSlugs: string[];
};

export function parseFilters(search: Record<string, unknown>): FilterState {
  const list = (value: unknown): string[] =>
    typeof value === 'string' && value.length > 0 ? value.split(',') : [];
  return {
    city: list(search.city),
    district: list(search.district),
    tag: list(search.tag),
  };
}

export function serializeFilters(
  state: FilterState,
): Record<string, string | undefined> {
  return {
    city: state.city.length ? state.city.join(',') : undefined,
    district: state.district.length ? state.district.join(',') : undefined,
    tag: state.tag.length ? state.tag.join(',') : undefined,
  };
}

export function toggleValue(list: string[], value: string): string[] {
  return list.includes(value)
    ? list.filter((entry) => entry !== value)
    : [...list, value];
}

export function hasAnyFilter(state: FilterState): boolean {
  return GROUPS.some((group) => state[group].length > 0);
}

function matchesExcept(
  place: FilterablePlace,
  state: FilterState,
  skip: FilterGroupKey | null,
): boolean {
  if (
    skip !== 'city' &&
    state.city.length &&
    !state.city.includes(place.citySlug)
  ) {
    return false;
  }
  if (
    skip !== 'district' &&
    state.district.length &&
    !state.district.includes(place.districtSlug)
  ) {
    return false;
  }
  if (
    skip !== 'tag' &&
    state.tag.length &&
    !state.tag.some((slug) => place.tagSlugs.includes(slug))
  ) {
    return false;
  }
  return true;
}

export function matchesFilters(
  place: FilterablePlace,
  state: FilterState,
): boolean {
  return matchesExcept(place, state, null);
}

type Option = { slug: string; label: string; count: number };

function facet(
  places: FilterablePlace[],
  state: FilterState,
  group: FilterGroupKey,
  candidates: { slug: string; label: string }[],
): Option[] {
  const scope = places.filter((place) => matchesExcept(place, state, group));
  return candidates
    .map((candidate) => ({
      ...candidate,
      count: scope.filter((place) => {
        if (group === 'city') return place.citySlug === candidate.slug;
        if (group === 'district') return place.districtSlug === candidate.slug;
        return place.tagSlugs.includes(candidate.slug);
      }).length,
    }))
    .filter((option) => option.count > 0 || state[group].includes(option.slug));
}

export function buildSections(
  places: FilterablePlace[],
  state: FilterState,
  cities: CitySummary[],
  tags: { slug: string; label: string }[],
  cuisineAction?: { label: string; onClick: () => void },
): FilterSection[] {
  const cityCandidates = cities.map((city) => ({
    slug: city.slug,
    label: city.name,
  }));
  // Districts only make sense scoped to a city -- their names collide otherwise.
  const districtCandidates = state.city.length
    ? cities
        .filter((city) => state.city.includes(city.slug))
        .flatMap((city) => city.districts)
        .map((district) => ({ slug: district.slug, label: district.name }))
    : [];

  const sections: FilterSection[] = [
    {
      kind: 'chips',
      id: 'city',
      label: 'Stad of regio',
      choices: facet(places, state, 'city', cityCandidates).map((option) => ({
        ...option,
        selected: state.city.includes(option.slug),
      })),
    },
  ];

  const districtOptions = facet(places, state, 'district', districtCandidates);
  if (districtOptions.length) {
    sections.push({
      kind: 'chips',
      id: 'district',
      label: 'Stadsdeel',
      hint: state.city.length
        ? `In ${cities
            .filter((city) => state.city.includes(city.slug))
            .map((city) => city.name)
            .join(', ')}`
        : undefined,
      choices: districtOptions.map((option) => ({
        ...option,
        selected: state.district.includes(option.slug),
      })),
    });
  }

  if (tags.length) {
    sections.push({
      kind: 'chips',
      id: 'tag',
      label: 'Gerecht of keuken',
      action: cuisineAction,
      choices: facet(places, state, 'tag', tags).map((option) => ({
        ...option,
        selected: state.tag.includes(option.slug),
      })),
    });
  }

  return sections;
}

export function activeChips(
  state: FilterState,
  cities: CitySummary[],
  tags: { slug: string; label: string }[],
): { id: string; label: string; group: FilterGroupKey }[] {
  const cityLabel = (slug: string) =>
    cities.find((city) => city.slug === slug)?.name ?? slug;
  const districtLabel = (slug: string) =>
    cities.flatMap((c) => c.districts).find((d) => d.slug === slug)?.name ??
    slug;
  const tagLabel = (slug: string) =>
    tags.find((tag) => tag.slug === slug)?.label ?? slug;

  return [
    ...state.tag.map((slug) => ({
      id: `tag:${slug}`,
      label: tagLabel(slug),
      group: 'tag' as const,
    })),
    ...state.district.map((slug) => ({
      id: `district:${slug}`,
      label: districtLabel(slug),
      group: 'district' as const,
    })),
    ...state.city.map((slug) => ({
      id: `city:${slug}`,
      label: cityLabel(slug),
      group: 'city' as const,
    })),
  ];
}
